// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { EditablePropertyButton } from '@/components/editable-property-button';
import { useDisclosure } from '@/hooks/use-disclosure';
import { Ticket } from '@/ticket/api/types';
import { TicketCloseDialog } from '@/ticket/components/dialogs/ticket-close-dialog';
import { TicketReopenDialog } from '@/ticket/components/dialogs/ticket-reopen-dialog';
import {
  TICKET_CATEGORY_DISPLAY_MAP,
  TICKET_STATUS_DISPLAY_MAP,
} from '@/ticket/util/labels';
import { Button, Chip, Stack } from '@mui/material';

interface Props {
  /**
   * The ticket to display the header for
   */
  ticket: Ticket;
  /**
   * Called when the edit title button is clicked
   */
  onEditTitle: () => void;
}

/**
 * Displays the header section of a ticket, including breadcrumbs,
 * title, status, category, and action buttons.
 */
export function TicketHeader({ ticket, onEditTitle }: Props) {
  const closeDialog = useDisclosure(false);
  const reopenDialog = useDisclosure(false);
  return (
    <Stack spacing={2}>
      <TicketCloseDialog
        close={closeDialog.close}
        isOpen={closeDialog.isOpen}
        ticket={ticket}
      />
      <TicketReopenDialog
        close={reopenDialog.close}
        isOpen={reopenDialog.isOpen}
        ticket={ticket}
      />
      {/* Title and Actions */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        paddingX={3}
        spacing={1}
      >
        <Stack direction="row" alignItems="flex-start">
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={1}
          >
            <EditablePropertyButton variant="h5" onClick={onEditTitle}>
              {ticket.title}
            </EditablePropertyButton>
            <Chip
              label={TICKET_STATUS_DISPLAY_MAP[ticket.status].label}
              color={TICKET_STATUS_DISPLAY_MAP[ticket.status].color}
              size="small"
            />
            <Chip
              label={TICKET_CATEGORY_DISPLAY_MAP[ticket.category]}
              size="small"
            />
          </Stack>
        </Stack>
        <Stack direction="row" spacing={1}>
          {ticket.status === 'OPEN' && (
            <>
              <Button
                size="small"
                variant="outlined"
                onClick={closeDialog.open}
              >
                Close ticket
              </Button>
            </>
          )}
          {(ticket.status === 'CLOSED' || ticket.status === 'COMPLETED') && (
            <>
              <Button
                size="small"
                variant="outlined"
                onClick={reopenDialog.open}
              >
                Reopen ticket
              </Button>
            </>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
}
